<script setup>
import {usePortalContentAndDesignStore} from "../../stores/partners/portalContentAndDesignStore";
import {ref, computed, watch} from "vue";
import EditableBtn from "./portal-editable-fields/EditableBtn.vue";
import EditableTextField from "./portal-editable-fields/EditableTextField.vue";
import ValidationErrors from "../common/ValidationErrors.vue";

    const portalContentAndDesignStore = usePortalContentAndDesignStore();

    const emit = defineEmits(['modal-off']);

    const addUrl = route('partners_int.custom_links.add');

    const props = defineProps({
        programLinks: {
            type: Array,
            default: () => []
        }
    })

   const key = ref('')
   const page_path = ref('')
   const error = ref(null)
   const loading = ref(false)
   const create_allowed = ref(false)
   const program_link_index = ref(0)
   const program_link = ref(props.programLinks[0])
   const createRefLinkModal = ref(null)


    const addCustomLink = () => {

        if (loading.value || !create_allowed.value) {
            return;
        }

        error.value = null;
        loading.value = true;

        axios
            .post(addUrl, {
                key: key.value,
                domain_id: program_link.value.id,
                is_additional: program_link.value.is_additional,
                page_url: page_link_active.value && normalized_page_path.value ? '/' + normalized_page_path.value : null
            })
            .then(response => {
                if (+response.data.status === 1) {
                    if(createRefLinkModal.value) {
                        createRefLinkModal.value.showModal = false;
                    }
                    portalContentAndDesignStore.resourcesPageSettings.custom_links.push(
                        response.data.data
                    );
                    emit('modal-off', 'partner_add_custom_link');
                } else {
                    error.value = response.data?.error || response.data?.errors || 'Failed to update custom link';
                }
            })
            .catch(resError => {
                if (resError.response && resError.response.status === 422) {
                    error.value = resError.response?.data?.errors || 'Something went wrong';
                } else {
                    console.log('Creating link error:', error.response);
                    error.value = resError.response?.data?.error || 'An error occurred. See console for details.';
                }
            }).finally(() => loading.value = false);
    }

    watch([key, page_path, program_link_index], () => {
        create_allowed.value = key.value.trim().replaceAll(' ', '').length > 4
            && (!page_link_active.value || normalized_page_path.value !== '');
    });

    const CUSTOM_PAGE_OPTION = 'custom_page';

    // A custom page URL is a main domain link with a path, so it uses the first (main domain) program link.
    const programLinkChange = (event) => {
            const value = event.target.value;
            program_link.value = value === CUSTOM_PAGE_OPTION ? props.programLinks[0] : props.programLinks[value];
    }

    const page_links_enabled = computed(() => !!program_link.value?.page_links);

    const custom_page_available = computed(() => !!props.programLinks[0]?.page_links);

    const page_link_active = computed(() => page_links_enabled.value && program_link_index.value === CUSTOM_PAGE_OPTION);

    const page_url_prefix = computed(() => page_links_enabled.value ? program_link.value.landing_url + '/' : '');

    const normalized_page_path = computed(() => page_path.value.trim().replace(/^\/+/, ''));

    const comparableHost = host => host.toLowerCase().replace(/^www\./, '');

    // A pasted full page URL on the program domain keeps only the part after the domain prefix.
    const onPagePathInput = () => {
        const input = page_path.value.trim();
        try {
            const url = new URL(/^https?:\/\//i.test(input) ? input : 'https://' + input);
            const landing = new URL(program_link.value.landing_url);
            if (comparableHost(url.hostname) === comparableHost(landing.hostname)) {
                page_path.value = (url.pathname + url.search).replace(/^\/+/, '');
            }
        } catch {
            // not a URL, keep the typed path as is
        }
    };

    const page_link = computed(() => {
        if (!page_link_active.value || !normalized_page_path.value) {
            return null;
        }

        try {
            const page = new URL('/' + normalized_page_path.value, program_link.value.landing_url);
            page.hash = '';
            page.searchParams.delete(program_link.value.ref_param);
            page.searchParams.append(program_link.value.ref_param, '');

            return page.toString();
        } catch {
            return null;
        }
    });

    const ref_link = computed(() => {
        return (page_link.value ?? program_link.value.url) + key.value;
    });

</script>
<template>
    <modal
       v-if="programLinks.length > 0"
       :title="portalContentAndDesignStore.translationSettings.portal_translation_features_referral_url"
       :cancel_btn_title="portalContentAndDesignStore.translationSettings.portal_translation_general_cancel"
       size="small"
       name="partner_add_custom_link"
       ref="createRefLinkModal"
    >
        <template #toggle>
            <editable-btn 
                :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                :corners="portalContentAndDesignStore.getPortalLayoutCornersClass"
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_features_create_new_ref_url"
            />
        </template>
        <template #body>
            <div class="space-y-4">
                <validation-errors :errors="error" />
                <div v-if="programLinks.length > 1 || custom_page_available" class="space-y-2">
                    <select id="program_custom_link" name="program_custom_link" v-model="program_link_index"
                            @change="programLinkChange"
                            class="block w-full rounded border-gray-300 sm:text-sm focus:ring-0 focus:border-gray-400">
                        <option v-for="(link, index) in programLinks" v-bind:value="index">
                            {{ link.domain }}
                        </option>
                        <option v-if="custom_page_available" :value="CUSTOM_PAGE_OPTION">
                            {{ portalContentAndDesignStore.translationSettings.portal_translation_features_page_url }}
                        </option>
                    </select>
                    <div v-if="page_link_active" class="flex items-center w-full rounded border border-gray-300 bg-white sm:text-sm focus-within:border-gray-400">
                        <span class="min-w-0 max-w-[60%] truncate py-2 pl-3 text-gray-400">{{ page_url_prefix }}</span>
                        <input type="text"
                            v-model="page_path"
                            id="page_url"
                            name="page_url"
                            placeholder="pricing"
                            class="min-w-0 flex-1 border-0 bg-transparent py-2 pl-0 pr-3 text-gray-900 sm:text-sm focus:ring-0"
                            @input="onPagePathInput"
                            v-on:keyup.enter="addCustomLink"
                        >
                    </div>
                </div>
                <div>
                    <editable-text-field
                        text-type="label"
                        for-attribute="identification"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_features_idetification_code"
                        class="block text-gray-900 text-sm mb-1.5"
                    />
                    <input type="text"
                        v-model="key"
                        id="identification"
                        name="identification"
                        class="block w-full rounded border-gray-300 sm:text-sm focus:ring-0 focus:border-gray-400"
                        v-on:keyup.enter="addCustomLink"
                    >
                </div>
                <div
                    class="py-3 px-4 text-sm bg-gray-100 text-gray-900 text-opacity-60 rounded flex items-center space-x-1.5">
                    <icon name="link"></icon>
                    <span>{{ ref_link }}</span>
                </div>
            </div>
        </template>
        <template #footer>
            <editable-btn v-on:click="addCustomLink"
                          :loading="loading"
                          :disabled="!create_allowed"
                          v-model="portalContentAndDesignStore.translationSettings.portal_translation_general_create"
            />
        </template>
    </modal>
</template>

